import { memo, useCallback, useState } from 'react';
import { DateInput, DateInputProps, TableListFieldItemValue } from '@page/ui';

interface DateInputFieldProps {
  value: string;
  actions?: React.ReactNode;
  editActions?: React.ReactNode;
  alwaysDisplayActions?: boolean;
  className?: string;
  name?: string;
  dateInputProps?: Omit<DateInputProps, 'value' | 'onChange' | 'name'>;
  renderDisplayValue?: (value: string) => React.ReactNode;
  onSave?: (value: string) => void;
  onEdit?: () => void;
  isEditing?: boolean;
  setIsEditing?: (isEditing: boolean) => void;
  clickToEdit?: boolean;
}

const DateInputFieldBase = ({
  value: defaultValue,
  actions,
  alwaysDisplayActions = false,
  editActions,
  className,
  name,
  dateInputProps,
  renderDisplayValue,
  onSave,
  onEdit,
  isEditing: externalIsEditing,
  setIsEditing: externalSetIsEditing,
  clickToEdit = true,
}: DateInputFieldProps) => {
  const [internalIsEditing, setInternalIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(defaultValue);

  const isControlled = externalIsEditing !== undefined;
  const isEditing = isControlled ? externalIsEditing : internalIsEditing;
  const setIsEditing = isControlled ? externalSetIsEditing : setInternalIsEditing;

  const handleEdit = useCallback(() => {
    setEditValue(defaultValue);
    setIsEditing?.(true);
    onEdit?.();
  }, [defaultValue, setIsEditing, onEdit]);

  const handleSave = useCallback(
    (immediateValueToSave?: string) => {
      setIsEditing?.(false);
      onSave?.(immediateValueToSave ?? editValue);
    },
    [editValue, setIsEditing, onSave],
  );

  const displayValue = renderDisplayValue ? renderDisplayValue(defaultValue) : defaultValue || ' ';

  return (
    <TableListFieldItemValue
      actions={actions}
      editActions={editActions}
      alwaysDisplayActions={alwaysDisplayActions}
      editingActionsClassName="right-10"
      className={className}
      isEditing={isEditing}
      onEdit={handleEdit}
      onSave={handleSave}
      renderEditMode={() => (
        <DateInput
          value={editValue}
          name={name}
          onChange={(value) => {
            setEditValue(value ?? '');
            handleSave(value ?? '');
          }}
          size="md"
          fieldClassName="h-[46px] p-3 font-body-md text-text-primary font-normal"
          popoverContentProps={{
            withinPortal: true,
            className: 'z-201',
          }}
          autoFocus
          {...dateInputProps}
        />
      )}
      clickToEdit={clickToEdit}
      saveOnClickOutside={false}
    >
      {displayValue}
    </TableListFieldItemValue>
  );
};

export const DateInputField = memo(DateInputFieldBase);
